import { LocationSearchItem } from '@deckee/api-app-main';
import {
  Box,
  Button,
  Card,
  Heading,
  MessageBlock,
  NavList,
  Paragraph,
} from '@deckee/deck-hand';
import React, { FC, MouseEvent, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { callEndpoint, telemetry } from '../../../../services/backend';
import {
  moveMapToLocation,
  setWeatherLocationSearch,
} from '../../../../services/store/modules/app-controls';
import { selectCurrentLocation } from '../../../../services/store/modules/geo-service';
import LocationSearch, {
  SplitLocationSearchResults,
} from '../../Plan/components/LocationSearchInput/LocationSearch';
import BasicDrawerLayout from '../../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import { LayoutBody } from '../../../layouts/components/LayoutBody/LayoutBody';
import LocationLockup from '../../../shared/LocationLockup/LocationLockup';
import { close, ReactSvg } from '../../../shared/vector-images';
import FavouriteStar from '../components/LocationSelector/FavouriteStar';
import {
  favouriteToSearchItem,
  findFavouriteForLocation,
  getFavouriteDisplayDescription,
  getSearchResultDisplay,
} from '../helpers/location/favouriteIdentity';
import {
  buildWeatherLocationFavouriteAnalyticsPayload,
  trackWeatherLocationFavouriteSelected,
} from '../helpers/analytics/weatherLocationFavouriteAnalytics';
import {
  trackWeatherForecastLocationSearchCancelled,
  trackWeatherForecastLocationSelected,
} from '../helpers/analytics/weatherForecastAnalytics';
import { getWeatherForecastDayFromSearch } from '../helpers/day/dayQuery';
import { getWeatherForecastHomePath } from '../helpers/location/searchPaths';
import { useWeatherLocationFavourites } from '../hooks/useWeatherLocationFavourites';

const WeatherSearchLayoutBody = styled(LayoutBody)`
  padding-top: 0;
  height: 100%;
  min-height: 0;
`;

const WeatherSearchCancelButton = styled(Button)`
  margin: 0;
  padding: 0;
  padding-left: ${({ theme }) => theme.space[2.5]}px;
`;

const MAX_GOOGLE_PLACES_RESULTS = 5;

const FAVOURITES_EMPTY_MESSAGE = 'Try adding some favorite locations';
const SEARCH_EMPTY_MESSAGE =
  'We couldn’t find any locations matching your search.';
const FAVOURITES_EMPTY_MAX_HEIGHT_PX = 240;

interface WeatherForecastLocationSearchPageProps {
  latitude: number | string;
  longitude: number | string;
  weatherType?: 'general' | 'swell';
}

interface NearbyWeatherStations {
  name: string;
  latitude: number;
  longitude: number;
  region: string;
  state: string;
  distance: number;
  distanceUnits: string;
  hasSwell?: boolean;
  hasTides?: boolean;
}

interface ReferenceWeatherStations {
  distance: number;
  distanceUnits: string;
  latitude: number;
  longitude: number;
  name: string;
  timeZone: string;
  utcOffset: number;
}

interface SearchItem {
  distance?: number;
  lat?: number;
  lon?: number;
  latitude?: number | string | null;
  longitude?: number | string | null;
  name?: string;
  title?: string;
  region?: string;
  state?: string;
  distanceMeters?: number;
  placeId?: string;
  placeDescription?: string;
  markerType?: string;
  markerId?: string;
  hasSwell?: boolean;
  hasTides?: boolean;
  favouriteId?: string;
}

const mapStationToSearchItem = (
  station: NearbyWeatherStations,
): SearchItem => ({
  title: station.name,
  name: station.name,
  lat: station.latitude,
  lon: station.longitude,
  latitude: station.latitude,
  longitude: station.longitude,
  region: station.region,
  state: station.state,
  distance: station.distance,
  hasSwell: station.hasSwell,
  hasTides: station.hasTides,
  placeId: undefined,
  markerType: undefined,
  markerId: undefined,
});

const WeatherForecastLocationSearchPage: FC<
  WeatherForecastLocationSearchPageProps
> = ({ latitude, longitude, weatherType }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const selectedDayDate = getWeatherForecastDayFromSearch(location.search);
  const { latitude: currentLat, longitude: currentLon } = useSelector(
    selectCurrentLocation,
  );

  const [isLoadingNearbyStations, setIsLoadingNearbyStations] = useState(true);
  const [closestWeatherStations, setClosestWeatherStations] = useState<
    SearchItem[]
  >([]);
  const [nearbyStationsError, setNearbyStationsError] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const {
    favourites,
    isLoading: favouritesLoading,
    loadError: favouritesLoadError,
    removeFavourite,
  } = useWeatherLocationFavourites();

  const isEmptySearch = searchTerm === '';

  useEffect(() => {
    dispatch(setWeatherLocationSearch());

    let isMounted = true;

    const loadClosestLocations = async () => {
      setIsLoadingNearbyStations(true);
      setNearbyStationsError(false);

      try {
        const queryWeatherSummaryParams = {
          latitude: +latitude,
          longitude: +longitude,
          type: weatherType || 'general',
        };

        const { nearbyWeatherStations: weatherStations } = await callEndpoint<{
          data: {
            referenceWeatherStation: ReferenceWeatherStations;
            nearbyWeatherStations: NearbyWeatherStations[];
          };
        }>(
          callEndpoint.endpoints.queryNearbyWeatherStations,
          {},
          queryWeatherSummaryParams,
        );

        if (!isMounted) {
          return;
        }

        telemetry(
          telemetry.DEBUG,
          `[WeatherForecastLocationSearchPage.getData] get the closest weather stations for location: ${JSON.stringify(
            weatherStations,
          )}`,
        );

        const locationsArray: SearchItem[] = (weatherStations ?? [])
          .filter(
            (value, index, self) =>
              index ===
              self.findIndex((station) => station.name === value.name),
          )
          .map(mapStationToSearchItem);

        const closestLocationsByDistance = locationsArray.sort(
          (a, b) => (a.distance || 0) - (b.distance || 0),
        );

        setClosestWeatherStations(closestLocationsByDistance);
      } catch (err) {
        if (!isMounted) {
          return;
        }

        setClosestWeatherStations([]);
        setNearbyStationsError(true);
        telemetry(
          telemetry.ERROR,
          `[WeatherForecastLocationSearchPage.getData] Error retrieving data ${JSON.stringify(
            err,
          )}`,
        );
      } finally {
        if (isMounted) {
          setIsLoadingNearbyStations(false);
        }
      }
    };

    loadClosestLocations();

    return () => {
      isMounted = false;
    };
  }, [dispatch, latitude, longitude, weatherType]);

  const renderPanelError = useCallback(
    (message: string) => (
      <MessageBlock type="error" size="small">
        {message}
      </MessageBlock>
    ),
    [],
  );

  const renderEmptyState = useCallback(
    (
      message = FAVOURITES_EMPTY_MESSAGE,
      showTitle = true,
      maxHeightPx?: number,
    ) => (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        px={4}
        py={maxHeightPx != null ? 4 : 6}
        maxHeight={maxHeightPx != null ? `${maxHeightPx}px` : undefined}
        overflow={maxHeightPx != null ? 'hidden' : undefined}
      >
        {showTitle && (
          <Heading level={4} mb={2}>
            Looks a little empty here
          </Heading>
        )}
        <Paragraph color="darkergrey" mb={4}>
          {message}
        </Paragraph>
        <FavouriteStar size={72} />
      </Box>
    ),
    [],
  );

  const renderSearchResultItem = useCallback(
    (
      item: LocationSearchItem | SearchItem,
      index: number,
      onSelect: (item: LocationSearchItem) => void,
    ) => {
      const searchItem = item as SearchItem;
      const { displayName, descriptionText } =
        getSearchResultDisplay(searchItem);
      const matchingFavourite = findFavouriteForLocation(
        favourites,
        searchItem,
      );

      return (
        <NavList.Item
          key={`${item.placeId || item.markerId || searchItem.favouriteId || index}`}
          onClick={() => onSelect(item as LocationSearchItem)}
          py={2}
          data-testid="weather-search-result"
        >
          <LocationLockup
            name={displayName}
            description={descriptionText}
            markerType={matchingFavourite ? undefined : searchItem.markerType}
            showIcon={!matchingFavourite}
            leadingImage={
              matchingFavourite ? (
                <FavouriteStar
                  filled
                  iconSlot
                  data-testid="weather-search-result-favourite-star"
                />
              ) : undefined
            }
          />
        </NavList.Item>
      );
    },
    [favourites],
  );

  const renderFavouriteItem = useCallback(
    (item: SearchItem, onSelect: (item: LocationSearchItem) => void) => {
      const favourite = favourites.find(
        (entry) => String(entry.id) === item.favouriteId,
      );

      return (
        <NavList.Item
          key={item.favouriteId}
          onClick={() => {
            if (favourite) {
              trackWeatherLocationFavouriteSelected(
                buildWeatherLocationFavouriteAnalyticsPayload({
                  source: 'weather search',
                  itemType: favourite.itemType,
                  latitude: favourite.latitude,
                  longitude: favourite.longitude,
                  markerType: favourite.markerType,
                }),
              );
            }

            onSelect(item as LocationSearchItem);
          }}
          py={2}
          width="100%"
          data-testid="weather-favourite-result"
        >
          <LocationLockup
            flex="1 1 auto"
            name={item.title || item.name || 'Unknown'}
            description={getFavouriteDisplayDescription({
              locationDescription: item.placeDescription,
              state: item.state,
              region: item.region,
              markerType: item.markerType,
            })}
            showIcon={false}
            leadingImage={<FavouriteStar filled iconSlot />}
          />
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flex="0 0 auto"
            onClick={(event: MouseEvent<HTMLDivElement>) => {
              event.stopPropagation();
              if (item.favouriteId) {
                removeFavourite(item.favouriteId, { source: 'weather search' });
              }
            }}
            data-testid="weather-favourite-remove"
          >
            <ReactSvg
              toDisplay={close()}
              options={{
                fillColor: theme.colors.mediumgrey,
              }}
              width={20}
              height={20}
            />
          </Box>
        </NavList.Item>
      );
    },
    [favourites, removeFavourite, theme.colors.mediumgrey],
  );

  const renderResults = useCallback(
    (
      items: LocationSearchItem[],
      onSelect: (item: LocationSearchItem) => void,
    ) => {
      if (isEmptySearch) {
        if (favouritesLoading || isLoadingNearbyStations) {
          return null;
        }

        const favouriteItems = favourites.map(favouriteToSearchItem);
        const hasFavourites = favouriteItems.length > 0;
        const hasNearbyStations = closestWeatherStations.length > 0;
        const showNearbyPanel = hasNearbyStations || nearbyStationsError;

        return (
          <>
            <Card mb={showNearbyPanel ? 3 : 0}>
              {hasFavourites && (
                <Card.Heading size="compact">Favorites</Card.Heading>
              )}
              <Card.Body>
                {favouritesLoadError && !hasFavourites ? (
                  renderPanelError(
                    'Unable to load favorites. You can still search for a location below.',
                  )
                ) : !hasFavourites ? (
                  renderEmptyState(
                    FAVOURITES_EMPTY_MESSAGE,
                    true,
                    FAVOURITES_EMPTY_MAX_HEIGHT_PX,
                  )
                ) : (
                  <NavList grouped edge>
                    {favouriteItems.map((item) =>
                      renderFavouriteItem(item, onSelect),
                    )}
                  </NavList>
                )}
              </Card.Body>
            </Card>
            {showNearbyPanel && (
              <Card>
                <Card.Heading size="compact">
                  Nearby weather stations
                </Card.Heading>
                <Card.Body>
                  {nearbyStationsError && !hasNearbyStations ? (
                    renderPanelError(
                      'Unable to load nearby weather stations. You can still search for a location below.',
                    )
                  ) : (
                    <NavList grouped edge>
                      {closestWeatherStations.map((item, index) =>
                        renderSearchResultItem(item, index, onSelect),
                      )}
                    </NavList>
                  )}
                </Card.Body>
              </Card>
            )}
          </>
        );
      }

      if (items.length === 0) {
        return renderEmptyState(SEARCH_EMPTY_MESSAGE, false);
      }

      return (
        <Card>
          <Card.Body>
            <NavList grouped edge>
              {items.map((item, index) =>
                renderSearchResultItem(item, index, onSelect),
              )}
            </NavList>
          </Card.Body>
        </Card>
      );
    },
    [
      favouritesLoading,
      isLoadingNearbyStations,
      closestWeatherStations,
      favourites,
      favouritesLoadError,
      isEmptySearch,
      nearbyStationsError,
      renderEmptyState,
      renderFavouriteItem,
      renderPanelError,
      renderSearchResultItem,
    ],
  );

  const renderSplitResults = useCallback(
    (
      results: SplitLocationSearchResults,
      onSelect: (item: LocationSearchItem) => void,
    ) => {
      const {
        googlePlaces,
        deckeeMarkers,
        storyMarkers = [],
        googlePlacesError,
        deckeeMarkersError,
      } = results;
      const googlePlacesResults = googlePlaces.slice(
        0,
        MAX_GOOGLE_PLACES_RESULTS,
      );
      const deckeeResults = [...storyMarkers, ...deckeeMarkers];
      const hasGooglePlaces = googlePlacesResults.length > 0;
      const hasDeckeeResults = deckeeResults.length > 0;
      const showGooglePanel = hasGooglePlaces || googlePlacesError;
      const showDeckeePanel = hasDeckeeResults || deckeeMarkersError;

      if (!showGooglePanel && !showDeckeePanel) {
        return (
          <Box
            border={`1px solid ${theme.colors.lightergrey}`}
            borderRadius="12px"
          >
            {renderEmptyState(SEARCH_EMPTY_MESSAGE, false)}
          </Box>
        );
      }

      return (
        <>
          {showGooglePanel && (
            <Card mb={showDeckeePanel ? 3 : 0}>
              <Card.Body>
                {googlePlacesError && !hasGooglePlaces ? (
                  renderPanelError(
                    'Unable to load place results. Other results may still be available below.',
                  )
                ) : (
                  <NavList grouped edge>
                    {googlePlacesResults.map((item, index) =>
                      renderSearchResultItem(item, index, onSelect),
                    )}
                  </NavList>
                )}
              </Card.Body>
            </Card>
          )}
          {showDeckeePanel && (
            <Card>
              {hasDeckeeResults && (
                <Card.Heading size="compact">Spots in DECKEE</Card.Heading>
              )}
              <Card.Body>
                {deckeeMarkersError &&
                  renderPanelError(
                    'Unable to load DECKEE spots. Other results may still be available above.',
                  )}
                {hasDeckeeResults && (
                  <NavList grouped edge>
                    {deckeeResults.map((item, index) =>
                      renderSearchResultItem(item, index, onSelect),
                    )}
                  </NavList>
                )}
              </Card.Body>
            </Card>
          )}
        </>
      );
    },
    [
      renderEmptyState,
      renderPanelError,
      renderSearchResultItem,
      theme.colors.lightergrey,
    ],
  );

  const navigateAfterSelection = useCallback(
    (lat: number, lon: number) => {
      history.push(getWeatherForecastHomePath(lat, lon, selectedDayDate));
    },
    [history, selectedDayDate],
  );

  const handleSelectSearchItem = useCallback(
    async (searchItem: LocationSearchItem) => {
      const item = searchItem as SearchItem;
      const { lat, lon, latitude: itemLat, longitude: itemLon, placeId } = item;

      let latLon = {
        lat:
          lat ||
          (typeof itemLat === 'number' ? itemLat : parseFloat(itemLat || '0')),
        lon:
          lon ||
          (typeof itemLon === 'number' ? itemLon : parseFloat(itemLon || '0')),
      };

      if (placeId) {
        latLon = await callEndpoint(
          callEndpoint.endpoints.locationsRetrieveDataForPlaceId,
          {
            placeId,
            fields: ['geometry'],
          },
        );
      }

      if (!latLon || !latLon.lat || !latLon.lon) {
        return;
      }

      if (!item.favouriteId) {
        trackWeatherForecastLocationSelected({
          latitude: latLon.lat,
          longitude: latLon.lon,
          name: item.title || item.name,
          markerType: item.markerType,
          placeId: item.placeId,
        });
      }

      await moveMapToLocation({
        center: latLon,
        zoom: 12,
      });

      navigateAfterSelection(latLon.lat, latLon.lon);
    },
    [navigateAfterSelection],
  );

  const handleClose = useCallback(() => {
    trackWeatherForecastLocationSearchCancelled();
    history.push(
      getWeatherForecastHomePath(latitude, longitude, selectedDayDate),
    );
  }, [history, latitude, longitude, selectedDayDate]);

  return (
    <BasicDrawerLayout showHeaderBar={false} showMenuBar={false}>
      <WeatherSearchLayoutBody>
        <LocationSearch
          onSelectLocation={handleSelectSearchItem}
          searchCenterLat={currentLat || +latitude}
          searchCenterLon={currentLon || +longitude}
          placeholder="Search"
          autoFocus={false}
          onSearchTermChange={setSearchTerm}
          renderResults={renderResults}
          wrapResultsInCard={false}
          splitSourceSearch={searchTerm.trim() !== ''}
          renderSplitResults={renderSplitResults}
          stickySearchInput
          searchInputTrailing={
            <WeatherSearchCancelButton variant="ghost" onClick={handleClose}>
              Cancel
            </WeatherSearchCancelButton>
          }
        />
      </WeatherSearchLayoutBody>
    </BasicDrawerLayout>
  );
};

export default WeatherForecastLocationSearchPage;
